import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'About Us', href: '/about-us' },
  { label: 'Products', href: '/products' },
  { label: 'Career', href: '/career' },
  { label: 'Media Room', href: '/media-room' },
];

const moreLinks = [
  { label: 'Contact Us', href: '/contact-us' },
  { label: 'Blog', href: 'https://kasturifoodproducts.com/blog/', external: true },
  { label: 'Privacy Policy', href: '#' },
  { label: 'Terms of Use', href: '#' },
];

const socialLinks = [
  { label: 'Facebook', href: 'https://www.facebook.com/kasturichhatua', icon: 'facebook' },
  { label: 'Instagram', href: 'https://www.instagram.com/kasturifoodproducts/', icon: 'instagram' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/kasturi-foods/', icon: 'linkedin' },
  { label: 'X', href: 'https://twitter.com/Kasturi_Foods', icon: 'twitter' },
  { label: 'YouTube', href: 'https://www.youtube.com/channel/UC5JnFdZjwrI3Rj2zrB6i0DA', icon: 'youtube' },
];

function SocialIcon({ type }: { type: string }) {
  if (type === 'facebook')
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      </svg>
    );
  if (type === 'instagram')
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    );
  if (type === 'twitter')
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
      </svg>
    );
  if (type === 'youtube')
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
        <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white" />
      </svg>
    );
  if (type === 'linkedin')
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.13 2.07 2.07 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45z" />
      </svg>
    );
  return null;
}

export default function Footer() {
  return (
    <footer
      className="border-t"
      style={{ background: '#2A1015', borderColor: 'rgba(255,255,255,0.08)' }}
    >
      {/* Main footer grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 items-start">

          {/* Column 1: Brand */}
          <div className="flex flex-col items-start">
            <AppImage
              src="/assets/images/kasturi_logo.png"
              alt="Kasturi Food Products logo"
              width={220}
              height={104}
              className="h-14 w-auto object-contain mb-5 self-start"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
            <p className="text-sm leading-relaxed mb-6 text-left" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Consider a perfect food brand for your home-cooked meals. Value your trust and rest on us.
            </p>

            {/* Contact details */}
            <div className="space-y-3 mb-7 w-full">
              <div className="flex items-start gap-2">
                <span style={{ color: 'rgba(255,255,255,0.35)' }}>📍</span>
                <p className="text-xs leading-relaxed text-left" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Plot 75, Bhagwanpur Industrial Estate, Bhubaneswar,<br />Odisha 751019
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span style={{ color: 'rgba(255,255,255,0.35)' }}>📞</span>
                <a
                  href="tel:9437694986"
                  className="text-xs transition-colors hover:text-white"
                  style={{ color: 'rgba(255,255,255,0.4)' }}
                >
                  9437694986
                </a>
              </div>
              <div className="flex items-center gap-2">
                <span style={{ color: 'rgba(255,255,255,0.35)' }}>✉️</span>
                <a
                  href="mailto:kasturiflourmill@gmail.com"
                  className="text-xs transition-colors hover:text-white"
                  style={{ color: 'rgba(255,255,255,0.4)' }}
                >
                  kasturiflourmill@gmail.com
                </a>
              </div>
            </div>

            {/* Social icons */}
            <div className="flex items-center gap-4">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="transition-all duration-200"
                  style={{ color: 'rgba(255,255,255,0.4)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#F9DC2A'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.4)'; }}
                >
                  <SocialIcon type={s.icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="flex flex-col">
            <h4
              className="text-xs font-semibold uppercase tracking-widest mb-6 md:mt-5"
              style={{ color: '#F9DC2A' }}
            >
              Quick Links
            </h4>
            <ul className="flex flex-col gap-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm transition-colors duration-200 hover:text-white"
                    style={{ color: 'rgba(255,255,255,0.5)' }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: More */}
          <div className="flex flex-col">
            <h4
              className="text-xs font-semibold uppercase tracking-widest mb-6 md:mt-5"
              style={{ color: '#F9DC2A' }}
            >
              More
            </h4>
            <ul className="flex flex-col gap-3">
              {moreLinks.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm transition-colors duration-200 hover:text-white"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      href={link.href}
                      className="text-sm transition-colors duration-200 hover:text-white"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>

      {/* Bottom bar */}
      <div
        className="border-t px-6 md:px-12 py-5"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
            © Kasturi Flour Mill Private Limited. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Powered by{' '}
              <a
                href="https://liqvd.asia"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:text-white"
                style={{ color: 'rgba(255,255,255,0.5)' }}
              >
                Liqvd Asia
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
